import { Button } from './button.js';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './dialog.js';

/**
 * Consequence copy for Views held in the workspace, which every member shares. Worded to
 * hold for both kinds: a Static View has members, a Dynamic View has none, and neither
 * loses a record when the View goes.
 */
export const WORKSPACE_VIEW_DELETE_DESCRIPTION =
  'This View is removed for everyone in the workspace. No records are deleted.';

export interface DeleteViewDialogProps {
  open: boolean;
  /** Named in the title so the dialog says which View is about to go. */
  viewName: string;
  /**
   * What deleting actually costs, on this surface.
   *
   * Required, with no default, because the surfaces genuinely differ: workspace Views are
   * shared server-side, while the Signals queue keeps its Views in localStorage and deletes
   * them for this browser alone. A default would have quietly told Signals users their
   * teammates were losing a View, which is what happened when this prop did not exist.
   * Pass WORKSPACE_VIEW_DELETE_DESCRIPTION for the shared case.
   */
  description: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending?: boolean;
}

/**
 * Confirms deleting a saved View. Mirrors the member/invitation removal dialog in
 * settings (title poses the question and names the target, description states the
 * consequence, destructive button repeats the verb) so the two destructive flows in
 * the app do not look like they came from different products.
 */
export function DeleteViewDialog({
  open,
  viewName,
  description,
  onOpenChange,
  onConfirm,
  pending = false,
}: DeleteViewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete {viewName}?</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={pending}>
            Delete View
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
